import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import { FormAlert, Icon, IfHasPermission } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Text } from '../Text/Text';
import { Button } from '../Button/Button';
import { DesignSystemControls } from './DesignSystemControls';
import { DesignSystemPreview } from './DesignSystemPreview';
import type { DesignSystem, DesignSystemPanelProps } from './DesignSystemPanel.type';
import { BGLoadingIndicator } from '../BGLoadingIndicator/BGLoadingIndicator';
import { Permissions } from '../../constants';


export const DesignSystemPanel: React.FC<DesignSystemPanelProps> = ({
  isOpen,
  isClosing = false,
  onClose,
  project,
  onDesignSystemChange,
  onUnsavedChanges,
}) => {
  const { user } = useAuth();
  const [designSystem, setDesignSystem] = useState<DesignSystem | null>(null);
  const [savedDesignSystem, setSavedDesignSystem] = useState<DesignSystem | null>(null);
  const [initialDesignSystem, setInitialDesignSystem] = useState<DesignSystem | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [options, setOptions] = useState<any>(null);
  const { projectId } = useParams();

  useEffect(() => {
    if (isOpen) {
    const initializeDesignSystem = async () => {
      await loadOptions();
      await loadDesignSystem();
    };
    
    initializeDesignSystem();
  }
  }, [isOpen]);

  const loadOptions = async () => {
    try {
      const response = await api.get('/design-system/options');
      setOptions(response.data);
    } catch (err) {
      console.error('Error loading design system options:', err);
      setOptions(null);
    }
  };

  useEffect(() => {
    let hasChanges = false;
    
    if (designSystem && initialDesignSystem) {
      hasChanges = JSON.stringify(designSystem) !== JSON.stringify(initialDesignSystem);
    }
    
    setHasUnsavedChanges(hasChanges);
    
    if (onUnsavedChanges) {
      onUnsavedChanges(hasChanges, saving, handleSave, handleDiscard);
    }
  }, [designSystem, initialDesignSystem, saving]);

  const loadDesignSystem = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/projects/${projectId}/design-system`);
      
      if (response.data) {
        const loadedDesign = { ...response.data };
        
        const isDefaultDesignSystem = loadedDesign.id === 'default';
        
        if (options?.fontSizePresets && loadedDesign.typography.fontSize) {
          loadedDesign.typography.fontSizeValues = options.fontSizePresets[loadedDesign.typography.fontSize];
        }
        if (options?.spacingPresets && loadedDesign.spacing.density) {
          loadedDesign.spacing.spacingValues = options.spacingPresets[loadedDesign.spacing.density];
        }
        
        if (isDefaultDesignSystem) {
          setDesignSystem(loadedDesign);
          setSavedDesignSystem(null);
          setInitialDesignSystem(loadedDesign);
          setHasUnsavedChanges(false);
          onDesignSystemChange?.(loadedDesign);
        } else {
          setDesignSystem(loadedDesign);
          setSavedDesignSystem(loadedDesign);
          setInitialDesignSystem(loadedDesign);
          setHasUnsavedChanges(false);
          onDesignSystemChange?.(loadedDesign);
        }
      }
    } catch (err: any) {
      console.error('Error loading design system:', err);
      
      const defaultSystem = getDefaultDesignSystem();
      setDesignSystem(defaultSystem);
      setSavedDesignSystem(null);
      setInitialDesignSystem(defaultSystem);
      setHasUnsavedChanges(false);
      onDesignSystemChange?.(defaultSystem);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultDesignSystem = (): DesignSystem => ({
    theme_name: 'default',
    colors: {
      background: '#FFFFFF',
      textColor: '#1A1A1A',
      mutedBg: '#F5F5F5',
      mutedText: '#737373',
      primaryBg: '#2563EB',
      primaryText: '#FFFFFF',
      secondaryBg: '#F5F5F5',
      secondaryText: '#1A1A1A',
      accentBg: '#8B5CF6',
      accentText: '#FFFFFF',
      destructiveBg: '#EF4444',
      destructiveText: '#FFFFFF',
      input: '#FFFFFF',
      border: '#E5E5E5',
      focusBorder: '#2563EB'
    },
    typography: {
      fontFamily: 'System Default',
      fontSize: 'medium',
      fontSizeValues: { base: 14, medium: 16, large: 20, xlarge: 24 }
    },
    spacing: {
      density: 'normal',
      spacingValues: { padding: 16, gap: 16, margin: 24 }
    },
    borders: { radiusRem: 0.5 },
    project_id: projectId ?? '',
    user_id: user?.id || '',
    created_at: Date.now(),
    updated_at: Date.now()
  });

  const handleSave = async () => {
    if (!designSystem) {
      setError('You do not have permission to save design system changes');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      let response;
      if (savedDesignSystem) {
        response = await api.put(`/projects/${projectId}/design-system`, designSystem);
      } else {
        response = await api.post(`/projects/${projectId}/design-system`, {
          ...designSystem,
          project_id: projectId
        });
      }

      setDesignSystem(response.data);
      setSavedDesignSystem(response.data);
      setInitialDesignSystem(response.data);
      setHasUnsavedChanges(false);
      onDesignSystemChange?.(response.data);
      setSuccessMessage('Design system saved successfully!');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Error saving design system:', err);
      setError('Failed to save design system');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (initialDesignSystem) {
      setDesignSystem({ ...initialDesignSystem });
      onDesignSystemChange?.(initialDesignSystem);
      setHasUnsavedChanges(false);
    }
  };

  const handleDesignSystemUpdate = (updated: DesignSystem) => {
    setDesignSystem(updated);
    onDesignSystemChange?.(updated);
  };

  if (!isOpen) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex flex-col h-full w-full bg-bg-primary rounded-bg-v2-lg overflow-hidden" style={{ boxShadow: '0px 8px 24px 0px rgba(9, 10, 20, 0.1)' }}>
        <div className="flex items-center justify-between p-bg-v2-xl bg-bg-primary border-b border-border-secondary min-h-[60px] relative">
          <div className="flex items-center gap-bg-v2-lg flex-1">
            <Icon name={IconName.Layout} size="sm" variant="secondary" />
            <Text variant="text-sm" weight="semibold" color="primary">
              Design System
            </Text>
          </div>
          <div className="flex items-center gap-0">
            <Button
              variant="ghost"
              size="small"
              label={<Icon name={IconName.Cross} size="sm" inheritColor />}
              onClick={onClose}
              className="!rounded-full !p-bg-v2-lg text-fg-secondary"
            />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-secondary-alt">
          <BGLoadingIndicator size="md" />
        </div>
      </div>
    );
  }

  if (!designSystem) {
    return (
      <div className="flex flex-col h-full w-full bg-bg-primary rounded-bg-v2-lg" style={{ boxShadow: '0px 8px 24px 0px rgba(9, 10, 20, 0.1)' }}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Text variant="text-md" color="error">Failed to load design system</Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-bg-primary rounded-bg-v2-lg overflow-hidden" style={{ boxShadow: '0px 8px 24px 0px rgba(9, 10, 20, 0.1)' }}>
      {/* Header */}
      <div className="flex flex-col gap-bg-v2-lg border-b border-border-secondary min-h-[60px] p-bg-v2-xl flex-shrink-0">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-bg-v2-lg flex-1">
            <Icon name={IconName.Layout} size="sm" variant="secondary" />
            <Text variant="text-sm" weight="semibold" color="primary">
              Design System
            </Text>
          </div>
          <div className="flex items-center gap-bg-v2-lg">
            <IfHasPermission requires={Permissions.UpdateProjectPermission} permissions={project?.permissions}>
              <Button
                variant="primary"
                size="small"
                label={"Save Changes"}
                onClick={handleSave}
                disabled={saving || !hasUnsavedChanges}
              />
              <Button
                variant="outline"
                size="small"
                label={"Discard"}
                onClick={handleDiscard}
                disabled={saving || !hasUnsavedChanges}
              />
            </IfHasPermission>
            <Button
              variant="ghost"
              size="small"
              label={<Icon name={IconName.Cross} size="sm" inheritColor />}
              onClick={onClose}
              className="!rounded-full !p-bg-v2-lg text-fg-secondary"
            />
          </div>
        </div>
      </div>

      {/* Main Content - Split Layout */}
      <div className="flex flex-1 bg-bg-primary min-h-0">
        {/* Left Panel - Controls */}
        <div className="w-80 flex-shrink-0 border-r border-border-secondary flex flex-col overflow-hidden">
          {/* Controls Header */}
          <div className="bg-bg-secondary border-b border-border-secondary flex-shrink-0" style={{ padding: 'var(--spacing-xl, 24px)' }}>
            {error && (
              <FormAlert
                variant="error"
                message={error}
                autoDismissTimeout={5000}
                autoDismiss={true}
                onDismiss={() => setError('')}
                className='mb-bg-v2-lg'
              />
            )}
            {successMessage && (
              <FormAlert
                className='mb-bg-v2-lg'
                variant="success"
                message={successMessage}
                autoDismissTimeout={5000}
                autoDismiss={true}
                onDismiss={() => setSuccessMessage('')}
              />
            )}
            <div>
              <Text variant="text-md" weight="semibold" color="primary">Customize</Text>
              <Text variant="text-xs" color="secondary" className="mt-bg-v2-xxs">
                Adjust your application's look and feel
              </Text>
            </div>

          </div>

          {/* Controls Content - Scrollable */}
          <div className="flex-1 overflow-y-auto modern-scrollbar min-h-0">
            <DesignSystemControls
              designSystem={designSystem}
              options={options}
              permissions={project?.permissions}
              onUpdate={handleDesignSystemUpdate}
            />
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="flex-1 overflow-hidden min-h-0">
          <DesignSystemPreview designSystem={designSystem} />
        </div>
      </div>
    </div>
  );
};

